import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import logo from "images/logo.png";
import "modules/authentication/login/login.scss";
import {
  Box,
  Card,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  FormControl,
  InputAdornment,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core";
import { getHomePage } from "modules/platform-components/utils/configuration";
import {
  apiPaths,
  getAdminData,
  getData,
  getInstutueApp,
  postData,
} from "modules/platform-components/api-integration-util";
import { setUserSubjects } from "modules/platform-components/utils/subject-scope";

import { forgetSchema } from "./helper";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    width: "100%",
    height: 50,
    textTransform: "capitalize",
  },
  back: {
    color: "#3366FF",
    fontSize: 14,
    cursor: "pointer",
  },
  errorMessage: {
    width: "100%",
    "& > p": {
      color: "red",
    },
  },
}));

const Login = () => {
  const classes = useStyles();
  const history = useHistory();
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [tenantIds, setTenantIds] = useState([]);

  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, errors }: any = useForm({
    validationSchema: forgetSchema,
  });

  const fetchTenantData = useCallback(async () => {
    try {
      const response = await getAdminData(apiPaths.getTenants);
      setTenantIds(response.result);
      if (response.result.length > 0) {
        setSelectedTenantId(response.result[0].tenantId);
      }
    } catch (error) {
      console.error("Error fetching QB data:", error);
    }
  }, []);

  useEffect(() => {
    fetchTenantData();
  }, [fetchTenantData]);

  const handlePasswordVisibility = () => setShowPassword(!showPassword);

  const onSubmit = async (data) => {
    try {
      postData(apiPaths.login, {
        data,
        headers: { Tenantid: selectedTenantId },
      })
        .then(async (val) => {
          if (val?.responseCode === 200 && val?.result instanceof Object) {
            const userData = val?.result;
            localStorage.setItem("user", JSON.stringify(userData));

            try {
              const permRes = await getInstutueApp(apiPaths.userPermission);
              if (permRes?.result?.finalUserPagePermissions) {
                const perms = permRes.result.finalUserPagePermissions;
                localStorage.setItem(
                  "permissions",
                  typeof perms === 'string' ? perms : JSON.stringify(perms)
                );
              }
            } catch (error) {
              console.error("Error fetching user permissions:", error);
            }

            try {
              const userId = userData?.userId;
              if (userId != null) {
                const subjRes = await getData(apiPaths.listUserSubjectMap, {
                  headers: {
                    tenantId: selectedTenantId,
                    userEmail: userData.email,
                  },
                });
                const mappings = Array.isArray(subjRes?.result)
                  ? subjRes.result
                  : [];
                const mySubjects = mappings
                  .filter(
                    (m: any) =>
                      Number(m.userId ?? m.user_id) === Number(userId)
                  )
                  .map((m: any) => Number(m.subjectId ?? m.subject_id))
                  .filter((n: number) => !Number.isNaN(n));
                setUserSubjects(mySubjects);
              } else {
                setUserSubjects([]);
              }
            } catch (error) {
              console.error("Error fetching user subjects:", error);
              setUserSubjects([]);
            }

            // Get the dynamic home page path
            const homePath = getHomePage();
            // Ensure it starts with '/' and add hash
            const formattedPath = homePath.startsWith("/")
              ? homePath
              : `/${homePath}`;

            // Redirect to the correct URL format
            try {
              history.push(formattedPath);
            } catch (error) {
            }
            window.location.reload();
          } else {
            setMessage(
              val?.msg ?? "email or password is incorrect.please check"
            );
          }
        })
        .catch((val) => {
          setMessage(val ?? "An error occurred. Please try again later.");
        });
    } catch (error) {
      setMessage("An error occurred. Please try again later.");
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.keyCode === 13) {
        handleSubmit(onSubmit)();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleSubmit, onSubmit]);

  const handleTenantChange = (e) => {
    setSelectedTenantId(e.target.value);
  };

  const handleBack = () => history.push("/forgot-password");

  return (
    <Box className="login-wrapper">
      <Card className="card-wrapper">
        <div className="card-inner inner-logo">
          <img src={logo} style={{ maxWidth: 260, height: 'auto', marginBottom: 12 }} alt="Arise Medical Academy" />
          <h1 className="welcome-heading">Academic Portal</h1>
          <p style={{ color: '#8492a6', fontSize: 13, margin: '4px 0 0', fontFamily: "'Inter', 'Lato', sans-serif" }}>Sign in to your account</p>
        </div>
        <div className="card-inner inner-form">
          <FormControl className={classes.formControl}>
            <InputLabel>Institute</InputLabel>
            <Select
              value={selectedTenantId}
              label="Institute"
              onChange={handleTenantChange}
            >
              {tenantIds.map((tenantId: any) => {
                return (
                  <MenuItem value={tenantId.tenantId}>
                    {tenantId.tenantName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <label className="form-label">Username or Email</label>
            <TextField
              variant="outlined"
              type="email"
              name="email"
              placeholder="Enter your username or email"
              inputRef={register}
              error={!!errors?.email}
              helperText={errors?.email?.message}
              size="small"
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <label className="form-label">Password</label>
            <TextField
              variant="outlined"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              inputRef={register}
              error={!!errors.password}
              helperText={errors.password?.message}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handlePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Box
            display="flex"
            justifyContent="flex-end"
            className={classes.errorMessage}
          >
            {message && <FormHelperText>{message}</FormHelperText>}
          </Box>

          <Button
            type="submit"
            variant="contained"
            className={classes.submit}
            onClick={handleSubmit(onSubmit)}
            sx={{
              backgroundColor: '#3366FF',
              '&:hover': { backgroundColor: '#2952CC' },
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Sign In
          </Button>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <Typography onClick={handleBack} className={classes.back}>
              Forgot Password
            </Typography>
          </Box>
        </div>
      </Card>
    </Box>
  );
};

export default Login;
